import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, RefreshCw } from 'lucide-react';
import { vouchersApi } from '../../services/api';

type Txn = {
  id: number;
  vch_id: number | null;
  vch_no: string | null;
  vch_type: string | null;
  vch_type_id: number | null;
  vch_date: string | null;
  is_opening?: boolean;
  /** Dated after the report's "To" date — listed for visibility but not netted. */
  beyond_as_of?: boolean;
  debit: number;
  credit: number;
  running_balance: number;
};

const fmt = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const displayDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

/** Drill-down popup for one outstanding bill: lists every voucher entry
 *  (Sales / Receipt / Credit Note …) tagged to the bill ref, with a running
 *  balance, so the user can see exactly what makes up the closing figure and
 *  jump into any voucher. */
export default function BillTransactionsModal({
  isOpen, onClose, ledgerId, billName, partyName, asOf,
}: {
  isOpen: boolean;
  onClose: () => void;
  ledgerId: number;
  billName: string;
  partyName?: string | null;
  /** The report's "To" date — entries dated later are shown but not netted,
   *  so this popup's closing always agrees with the Outstanding row. */
  asOf?: string;
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Txn[]>([]);
  const [closing, setClosing] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    setLoading(true);
    setError(null);
    vouchersApi.getBillTransactions(ledgerId, billName, asOf)
      .then(res => {
        if (!alive) return;
        if (res.success) {
          setRows(res.data.transactions || []);
          setClosing(res.data.closing || 0);
        } else {
          setError('Could not load transactions');
        }
      })
      .catch(() => { if (alive) setError('Could not load transactions'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isOpen, ledgerId, billName, asOf]);

  if (!isOpen) return null;

  const openVoucher = (vchId: number | null) => {
    if (!vchId) return;
    onClose();
    navigate(`/billing/vouchers/edit/${vchId}`);
  };

  // Totals mirror the closing figure: only entries on/before the report's "To"
  // date count. Anything later is listed greyed-out so a mis-dated voucher is
  // obvious instead of looking like the bill is settled.
  const counted = rows.filter(r => !r.beyond_as_of);
  const excluded = rows.filter(r => r.beyond_as_of);
  const totalDebit = counted.reduce((s, r) => s + (r.debit || 0), 0);
  const totalCredit = counted.reduce((s, r) => s + (r.credit || 0), 0);
  const positive = closing >= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Bill Transactions</div>
            <div className="text-[17px] font-bold text-slate-800 truncate">{billName}</div>
            {partyName && <div className="text-[13px] text-slate-500 truncate">{partyName}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-2 sm:px-4 py-3">
          {loading ? (
            <div className="py-14 text-center text-slate-400 text-sm">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue-400" /> Loading…
            </div>
          ) : error ? (
            <div className="py-14 text-center text-red-500 text-sm">{error}</div>
          ) : rows.length === 0 ? (
            <div className="py-14 text-center text-slate-400 text-sm">No transactions tagged to this bill.</div>
          ) : (
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  <th className="text-left  px-2 py-1.5 border border-slate-200 font-semibold">Date</th>
                  <th className="text-left  px-2 py-1.5 border border-slate-200 font-semibold">Vch No</th>
                  <th className="text-left  px-2 py-1.5 border border-slate-200 font-semibold">Type</th>
                  <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Debit</th>
                  <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Credit</th>
                  <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className={r.beyond_as_of ? 'bg-amber-50/60 text-slate-400' : 'hover:bg-blue-50'}>
                    <td className="px-2 py-1.5 border border-slate-200 whitespace-nowrap tabular-nums text-slate-600">
                      {displayDate(r.vch_date)}
                      {r.beyond_as_of && (
                        <span className="ml-1 px-1 py-px rounded bg-amber-100 text-amber-700 text-[10px] font-semibold align-middle"
                          title={`Dated after the report's To date — not included in the closing balance`}>
                          after {displayDate(asOf)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 border border-slate-200 whitespace-nowrap">
                      {r.vch_id ? (
                        <button onClick={() => openVoucher(r.vch_id)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium">
                          {r.vch_no || `#${r.vch_id}`}<ExternalLink size={11} />
                        </button>
                      ) : (
                        <span className={r.is_opening ? 'text-slate-500 italic' : 'text-slate-500'}>{r.vch_no || '—'}</span>
                      )}
                    </td>
                    <td className={`px-2 py-1.5 border border-slate-200 whitespace-nowrap ${r.is_opening ? 'text-slate-500 italic' : 'text-slate-600'}`}>{r.vch_type || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums text-slate-700">{r.debit ? fmt(r.debit) : ''}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums text-slate-700">{r.credit ? fmt(r.credit) : ''}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums font-medium text-slate-800">
                      {r.beyond_as_of
                        ? <span className="text-slate-400">—</span>
                        : <>{fmt(Math.abs(r.running_balance))} {r.running_balance >= 0 ? 'Dr' : 'Cr'}</>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold text-slate-700">
                  <td colSpan={3} className="px-2 py-1.5 border border-slate-200 text-right">Total</td>
                  <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums">{fmt(totalDebit)}</td>
                  <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums">{fmt(totalCredit)}</td>
                  <td className={`px-2 py-1.5 border border-slate-200 text-right tabular-nums ${positive ? 'text-emerald-700' : 'text-red-600'}`}>
                    {fmt(Math.abs(closing))} {positive ? 'Dr' : 'Cr'}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {!loading && !error && excluded.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 leading-relaxed">
              <strong>{excluded.length} entr{excluded.length === 1 ? 'y is' : 'ies are'} dated after {displayDate(asOf)}</strong> (the
              report's <em>To</em> date), so {excluded.length === 1 ? 'it is' : 'they are'} not netted off this bill and it still shows
              as due. If the date is wrong, open the voucher and correct it — otherwise extend the report's <em>To</em> date.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <span className="text-[12px] text-slate-400">
            {rows.length} transaction{rows.length === 1 ? '' : 's'}
            {excluded.length > 0 && <> ({excluded.length} excluded)</>} • Closing
            {asOf && <> as on {displayDate(asOf)}</>}{' '}
            <span className={positive ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
              ₹{fmt(Math.abs(closing))} {positive ? 'Dr' : 'Cr'}
            </span>
          </span>
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] font-medium">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
